/**
 * UserList Page - Displays all users with their roles
 *
 * Displays a table of all users with roles, edit and delete actions.
 *
 * Requirements: 2.1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Spinner, SpinnerSize, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import {
  AddRegular,
  EditRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
import { AppHeader } from '@shared/components';
import { usePermissions } from '@shared/auth';
import type { AppRole } from '@shared/auth';
import { listUsers, deleteUser } from '../api';
import type { ManagedUser } from '../api';
import { useAdminContext } from '../AdminPage';
import styles from './UserList.module.css';

/**
 * Format role for display
 */
function formatRole(role: AppRole): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'policy_navigator':
      return 'Policy Navigator';
    case 'evidence_extractor':
      return 'Evidence Extractor';
    default:
      return role;
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

export const UserList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken } = usePermissions();
  const { refreshTrigger } = useAdminContext();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogUser, setDeleteDialogUser] = useState<ManagedUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const result = await listUsers(token);

      if (result.success) {
        setUsers(result.data);
      } else {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, refreshTrigger]);

  // Also refetch when component mounts (after navigation)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 200);

    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const handleEdit = (userId: string) => {
    navigate(`/admin/users/${userId}/edit`);
  };

  const handleDeleteClick = (user: ManagedUser) => {
    setDeleteDialogUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialogUser) return;

    setIsDeleting(true);
    try {
      const token = await getAccessToken();
      const result = await deleteUser(token, deleteDialogUser.id);

      if (result.success) {
        setUsers(users.filter(u => u.id !== deleteDialogUser.id));
        setDeleteDialogUser(null);
      } else {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogUser(null);
  };

  return (
    <div className={styles.container}>
      <AppHeader currentApp="admin" />
      <main className={styles.main}>
        <div className={styles.headerSection}>
          <h1 className={styles.title}>User Management</h1>
          <Link to="/admin/users/new" className={styles.createButton}>
            <AddRegular />
            <span>Create User</span>
          </Link>
        </div>

        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.large} label="Loading users..." />
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <button className={styles.retryButton} onClick={fetchUsers}>
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyContainer}>
            <h2 className={styles.emptyTitle}>No Users Found</h2>
            <p className={styles.emptyText}>
              Create your first user to get started.
            </p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <div className={styles.roleBadges}>
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className={`${styles.roleBadge} ${styles[role]}`}
                          >
                            {formatRole(role)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{formatDate(user.lastLogin)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleEdit(user.id)}
                          aria-label={`Edit ${user.name}`}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <EditRegular />
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.delete}`}
                          onClick={() => handleDeleteClick(user)}
                          aria-label={`Delete ${user.name}`}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <DeleteRegular />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          hidden={!deleteDialogUser}
          onDismiss={handleDeleteCancel}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Delete User',
            subText: deleteDialogUser
              ? `Are you sure you want to delete ${deleteDialogUser.name}? This action cannot be undone.`
              : '',
          }}
        >
          <DialogFooter>
            <PrimaryButton
              onClick={handleDeleteConfirm}
              text={isDeleting ? 'Deleting...' : 'Delete'}
              disabled={isDeleting}
            />
            <DefaultButton
              onClick={handleDeleteCancel}
              text="Cancel"
              disabled={isDeleting}
            />
          </DialogFooter>
        </Dialog>
      </main>
    </div>
  );
};
